import { ImportOutlined, SearchOutlined } from '@ant-design/icons'
import { requestGetTeamPrizeByEventId } from 'app/api/event'
import { IActionEvent, IEvent, IPrizeActionEvent } from 'app/api/event/model'
import { ENV_CONFIG } from 'app/common/config'
import { PermissionEnum } from 'app/common/enum'
import { BaseButton } from 'app/components/common/BaseButton'
import { BaseCol } from 'app/components/common/BaseCol'
import { BaseRow } from 'app/components/common/BaseRow'
import { BaseSpace } from 'app/components/common/BaseSpace'
import BaseText from 'app/components/common/BaseText'
import { BaseForm } from 'app/components/common/forms/BaseForm'
import { BaseInput } from 'app/components/common/inputs/BaseInput'
import { BaseTableManagement } from 'app/components/tables/BaseTableManagement'
import { convertColumnTable } from 'app/components/tables/BaseTableReport/hook'
import { useVerifyPermission } from 'app/helpers/permission.helper'
import { handleDownloadExcel } from 'app/service/general/service'
import { isEmpty } from 'lodash'
import { ResponseType } from 'parkway-web-common'
import { useEffect, useMemo, useState } from 'react'

interface ITeamPrizeInEvent {
  id?: number
  action: IActionEvent
  reward?: IPrizeActionEvent
  name?: string
  description?: string
}

export const ListTeamPrizeLayout = ({ event }: { event?: IEvent }) => {
  const [data, setData] = useState<ITeamPrizeInEvent[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [search, setSearch] = useState<string>('')

  const getData = async () => {
    if (!event?.id) return
    setLoading(true)
    try {
      const res: ResponseType<ITeamPrizeInEvent[]> =
        await requestGetTeamPrizeByEventId({
          id: event.id,
        })

      setData(res?.data)
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      getData()
    }, 1000)

    return () => clearTimeout(timer)
  }, [event])

  const dataSource = useMemo(() => {
    return data
      ?.filter(itm => {
        if (isEmpty(search)) return true

        const searchLowerCase = search.toLowerCase()

        if (itm?.name?.toLowerCase().includes(searchLowerCase)) return true

        if (itm?.reward?.name?.toLowerCase().includes(searchLowerCase))
          return true
      })
      ?.map((item, index) => {
        return {
          ...item,
          key: index + 1,
          index: index + 1,
        }
      })
  }, [data, search])

  const downloadExcel = async (): Promise<void> => {
    if (!event?.id) return

    await handleDownloadExcel({
      params: {
        id: event.id,
      },
      urlMethod: `${ENV_CONFIG.API_ENDPOINT}admin/reward/team/event-id/download`,
      fileName: 'Danh sách đội/ nhóm đạt giải',
    })
  }

  const { verifyPermission } = useVerifyPermission()

  return (
    <BaseSpace>
      <BaseRow gutter={16} align={'middle'} justify={'space-between'}>
        <BaseCol>
          <BaseText
            children={'Danh sách đội/nhóm đoạt giải'}
            fontWeight="semibold"
            fontSize="xs"
          />
        </BaseCol>
        {verifyPermission(PermissionEnum.ExportEvent) ? (
          <BaseCol>
            <BaseButton
              icon={<ImportOutlined rev={undefined} />}
              onClick={downloadExcel}
            >
              {'Export to excel'}
            </BaseButton>
          </BaseCol>
        ) : null}
      </BaseRow>

      <BaseRow>
        <BaseCol xl={8}>
          <BaseForm>
            <BaseForm.Item
              hiddenLabel
              style={{
                marginBottom: 0,
              }}
            >
              <BaseInput
                onChange={e => setSearch(e?.target?.value)}
                value={search}
                placeholder="Nhập tên đoàn viên, tên đơn vị, giải ..."
                allowClear
                suffix={<SearchOutlined rev={undefined} />}
              />
            </BaseForm.Item>
          </BaseForm>
        </BaseCol>
      </BaseRow>

      <BaseTableManagement
        dataSource={dataSource}
        loading={loading}
        pagination={{
          pageSize: 5,
        }}
        columns={[
          convertColumnTable({
            key: 'index',
            title: 'STT',
            classNameWidthColumnOverwrite: 'number-column',
            fixed: 'left',
            render: (value: number) => {
              return (
                <BaseText children={value} opacity="0.7" fontWeight="medium" />
              )
            },
          }),
          convertColumnTable<ITeamPrizeInEvent>({
            key: 'name',
            title: 'Tên đội/nhóm',
            fixed: 'left',
            classNameWidthColumnOverwrite: 'big-column',
            render: (_, record?: ITeamPrizeInEvent) => {
              return (
                <BaseText
                  children={record?.name ?? '-'}
                  opacity="0.7"
                  fontWeight="medium"
                />
              )
            },
          }),
          convertColumnTable<ITeamPrizeInEvent>({
            key: 'description',
            title: 'Mô tả',
            render: (_, record?: ITeamPrizeInEvent) => {
              return (
                <BaseText
                  children={record?.description ?? '-'}
                  opacity="0.7"
                  fontWeight="medium"
                />
              )
            },
          }),

          convertColumnTable<ITeamPrizeInEvent>({
            key: 'reward',
            title: 'Đạt giải',
            render: (_, record?: ITeamPrizeInEvent) => {
              return (
                <BaseText
                  children={record?.reward?.name ?? '-'}
                  opacity="0.7"
                  fontWeight="medium"
                />
              )
            },
          }),
        ]}
        className="row-overwrite-style"
        scroll={{
          x: true,
        }}
      />
    </BaseSpace>
  )
}
